import type { ComponentProps } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import ChatMessageEmojiInput from '@/components/Chat/ChatMessageEmojiInput';
import { ChatMessageEmojiInputFragment } from '@/components/Chat/ChatMessageEmojiInput.utils';
import Box from '@/components/UI/Box';
import Pressable from '@/components/Unistyles/Pressable';
import { type FragmentOf, readFragment } from '@/gql/graphql';

export const Item = ({
  children,
  ...props
}: ComponentProps<typeof Pressable>) => {
  return (
    <Pressable {...props} style={[style.item, props.style]}>
      {children}
    </Pressable>
  );
};

function Container({ children }: { children: React.ReactNode }) {
  return <Box style={style.container}>{children}</Box>;
}

export default function ChatMessageItemReaction({
  fragment,
  children,
}: {
  fragment: FragmentOf<typeof ChatMessageEmojiInputFragment>;
  children?: React.ReactNode;
}) {
  const message = readFragment(ChatMessageEmojiInputFragment, fragment);

  const reactions =
    (message.reaction_count as { [key: string]: number }[]) || [];

  return (
    <Container>
      {reactions.map((reaction) => {
        const [content, count] = Object.entries(reaction)[0] || [];

        if (!content || typeof count !== 'number') {
          return null;
        }

        return (
          <ChatMessageEmojiInput
            key={content}
            emoji={content}
            fragment={fragment}
          />
        );
      })}
      {children}
    </Container>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    padding: theme.space('xs'),
    flexDirection: 'row',
    gap: theme.space('sm'),
    flexWrap: 'wrap',
  },
  item: {
    paddingVertical: theme.space('sm'),
    paddingHorizontal: theme.space('md'),
    borderRadius: theme.borderRadius.xl,
    borderColor: theme.colors.borderMuted,
    borderWidth: theme.size(0.5),
    minWidth: theme.size(12),
    alignItems: 'center',
    gap: theme.space('sm'),
    flexDirection: 'row',
    justifyContent: 'center',
  },
}));
